'use memo';

import { c as _c } from "react/compiler-runtime";
import { Link } from '@tanstack/react-router';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { DataTableColumnHeader } from '@/components/DataTable';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { DateDurationCell } from '@/features/contract-and-cost/cells/DateDurationCell';
import { useFormatDate, useFormatDistance } from '@/hooks/date';
import { actionsColumn } from '../../-components/-table/actionsColumn';
import { contractNoColumn } from '../../-components/-table/contractNoColumn';
import { contractStatusColumn } from '../../-components/-table/contractStatusColumn';
import { useContractTypeLabels } from './-contractTypeLabels';
import type { ContractAlertItem } from './-query';
const toValidDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
function EndDateCell(t0) {
  "use memo";

  const $ = _c(23);
  const {
    getValue
  } = t0;
  const formatDate = useFormatDate();
  const formatDistance = useFormatDistance();
  const [now] = useState(_temp);
  let T0;
  let T1;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== formatDate || $[1] !== formatDistance || $[2] !== getValue || $[3] !== now) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const date = toValidDate(getValue());
      if (!date) {
        let t7;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t7 = <Text variant="tableItem">–</Text>;
          $[12] = t7;
        } else {
          t7 = $[12];
        }
        t6 = t7;
        break bb0;
      }
      T1 = VStack;
      t4 = "gap-0.5";
      t5 = <Text variant="label" className="text-neutral-on-fill">{formatDate(date, {
          preset: "date"
        })}</Text>;
      T0 = Text;
      t1 = "labelSmallReg";
      t2 = "text-warning-on-neutral-fill";
      t3 = formatDistance(date, now);
    }
    $[0] = formatDate;
    $[1] = formatDistance;
    $[2] = getValue;
    $[3] = now;
    $[4] = T0;
    $[5] = T1;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    T0 = $[4];
    T1 = $[5];
    t1 = $[6];
    t2 = $[7];
    t3 = $[8];
    t4 = $[9];
    t5 = $[10];
    t6 = $[11];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[13] !== T0 || $[14] !== t1 || $[15] !== t2 || $[16] !== t3) {
    t7 = <T0 variant={t1} className={t2}>{t3}</T0>;
    $[13] = T0;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== T1 || $[19] !== t4 || $[20] !== t5 || $[21] !== t7) {
    t8 = <T1 className={t4}>{t5}{t7}</T1>;
    $[18] = T1;
    $[19] = t4;
    $[20] = t5;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}
function _temp() {
  return new Date();
}
export function useAlertColumns(): ColumnDef<ContractAlertItem>[] {
  const {
    labelFor
  } = useContractTypeLabels();
  return [contractNoColumn<ContractAlertItem>(), {
    id: 'contractTitle',
    accessorKey: 'contractTitle',
    meta: {
      i18nKey: 'contractTitle',
      thStyle: {
        minWidth: '220px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    sortingFn: 'alphanumeric',
    cell: ({
      row
    }) => <Link to="/contract/$id" params={{
      id: row.original.contractId
    }} className="hover:underline">
          <Text variant="tableItem" className="font-semibold text-inherit">
            {row.original.contractTitle ?? '-'}
          </Text>
        </Link>
  }, contractStatusColumn<ContractAlertItem>(), {
    id: 'contractType',
    accessorKey: 'contractType',
    meta: {
      i18nKey: 'contractType',
      thStyle: {
        minWidth: '150px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: false,
    cell: ({
      row
    }) => <Text variant="tableItem">{labelFor(row.original.contractType)}</Text>
  }, {
    id: 'contractStartDate',
    accessorKey: 'contractStartDate',
    meta: {
      i18nKey: 'contractStartDate',
      thStyle: {
        minWidth: '170px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    cell: ({
      getValue
    }) => {
      const date = toValidDate(getValue<string | null>());
      return date ? <DateDurationCell date={date} /> : <Text variant="tableItem">–</Text>;
    }
  }, {
    id: 'contractEndDate',
    accessorKey: 'contractEndDate',
    meta: {
      i18nKey: 'contractEndDate',
      thStyle: {
        minWidth: '170px'
      }
    },
    header: DataTableColumnHeader,
    enableSorting: true,
    cell: props => <EndDateCell getValue={props.getValue} />
  }, actionsColumn<ContractAlertItem>()];
}